import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import modalStyles from './EventModal.module.css';
import axios from 'axios';
import { FaTimes, FaCalendarAlt, FaMapMarkerAlt, FaUsers, FaInfoCircle, FaExpand, FaCompress } from 'react-icons/fa';
import { useUser } from '../context/UserContext';
import { canManageProgram } from '../hooks/useProgramsApi';
import TeamSelector from './TeamSelector/TeamSelector';
import LocationPicker from './LocationPicker/LocationPicker';

const EventModal = ({ onClose, programId, eventToEdit, clubId }) => {
  const { userClubs } = useUser();
  const [isMaximized, setIsMaximized] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'Practice',
    startDateTime: '',
    endDateTime: '',
    location: {
      name: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        coordinates: {
          type: 'Point',
          coordinates: [0, 0],
        },
      },
    },
    opponentTeam: null, // ✅ KEEP for backward compatibility
    plannedAttendees: 0,
    status: 'Planned',
    // ✅ NEW: Team selection structure
    teams: {
      home: { type: 'custom', teamId: null, name: '' },
      away: { type: 'custom', teamId: null, name: '' }
    }
  });

  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [teams, setTeams] = useState([]); // Keep for legacy compatibility
  const [programTeams, setProgramTeams] = useState([]); // ✅ NEW: Program teams for selection
  const [teamSelectionEnabled, setTeamSelectionEnabled] = useState(false); // ✅ NEW: Feature flag
  const [programPermissions, setProgramPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Authorization logic - more restrictive to prevent backend permission issues
  const canManage = React.useMemo(() => {
    // Check if user has club management role
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(clubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      return isTargetClub && isOwnerOrManager;
    });

    // Additional check: Look for program-specific management roles in userClubs
    const hasProgramRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const role = (m.role || '').toLowerCase();
      // Only allow specific high-level program roles, not regular members
      const isProgramManager = ['programdirector', 'programadmin', 'headcoach'].includes(role);
      return isProgramManager;
    });

    // Check if user has program management permissions (but be more restrictive)
    const hasProgramPermissions = canManageProgram(programPermissions);
    
    // Additional validation: Even if backend says they have permissions,
    // double-check they're not just a regular member
    const isOnlyMember = Array.isArray(userClubs) && userClubs.every((m) => {
      const role = (m.role || '').toLowerCase();
      return role === 'member' || role === 'player' || role === 'observer';
    });
    
    // If user is only a member across all clubs, deny access regardless of backend permissions
    if (isOnlyMember) {
      return false;
    }

    return hasClubRole || hasProgramRole || hasProgramPermissions;
  }, [userClubs, clubId, programPermissions]);

  // Fetch program permissions for current user
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/permissions`);
        if (response.ok) {
          const data = await response.json();
          setProgramPermissions(data?.permissions || []);
        } else {
          console.log('Permissions endpoint not available, using club role only');
          setProgramPermissions([]);
        }
      } catch (error) {
        console.error('Error fetching program permissions:', error);
        setProgramPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchProgramPermissions();
  }, [programId]);

  // ✅ NEW: Fetch program teams for selection
  useEffect(() => {
    const fetchProgramTeams = async () => {
      if (!programId) return;
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/events/programs/${programId}/teams`
        );
        
        if (response.ok) {
          const data = await response.json();
          setProgramTeams(data.teams || []);
          setTeamSelectionEnabled(true);
          console.log('✅ Team selection enabled:', data.teams?.length || 0, 'teams available');
        } else {
          // Gracefully degrade - just don't show team selection
          console.log('Team selection not available for this program');
          setProgramTeams([]);
          setTeamSelectionEnabled(false);
        }
      } catch (error) {
        console.error('Error fetching program teams:', error);
        setProgramTeams([]);
        setTeamSelectionEnabled(false);
      }
    };

    fetchProgramTeams();
  }, [programId]);

  // Helper function to convert UTC to local datetime string for input
  const formatDateTimeForInput = (utcDateString) => {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);
    // Get local datetime in YYYY-MM-DDTHH:MM format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Update formData if editing an event
  useEffect(() => {
    if (eventToEdit) {
      setFormData({
        title: eventToEdit.title || '',
        description: eventToEdit.description || '',
        type: eventToEdit.type || 'Practice',
        startDateTime: formatDateTimeForInput(eventToEdit.dateTime?.start),
        endDateTime: formatDateTimeForInput(eventToEdit.dateTime?.end),
        location: {
          name: eventToEdit.location?.name || '',
          address: {
            street: eventToEdit.location?.address?.street || '',
            city: eventToEdit.location?.address?.city || '',
            state: eventToEdit.location?.address?.state || '',
            zipCode: eventToEdit.location?.address?.zipCode || '',
            coordinates:
              eventToEdit.location?.address?.coordinates || {
                type: 'Point',
                coordinates: [0, 0],
              },
          },
        },
        opponentTeam: eventToEdit.opponentTeam || null, // ✅ KEEP for backward compatibility
        plannedAttendees: eventToEdit.metrics?.plannedAttendees || 0,
        status: eventToEdit.status || 'Planned',
        // ✅ NEW: Handle both new teams structure and legacy opponentTeam
        teams: eventToEdit.teams || {
          home: { 
            type: 'custom', 
            teamId: null, 
            name: eventToEdit.opponentTeam ? 'Home Team' : '' 
          },
          away: { 
            type: 'custom', 
            teamId: null, 
            name: eventToEdit.opponentTeam || '' 
          }
        }
      });
    } else {
      // Reset to default values when adding a new event
      setFormData({
        title: '',
        description: '',
        type: 'Practice',
        startDateTime: '',
        endDateTime: '',
        location: {
          name: '',
          address: {
            street: '',
            city: '',
            state: '',
            zipCode: '',
            coordinates: {
              type: 'Point',
              coordinates: [0, 0],
            },
          },
        },
        opponentTeam: null, // ✅ KEEP for backward compatibility
        plannedAttendees: 0,
        status: 'Planned',
        // ✅ NEW: Initialize teams structure
        teams: {
          home: { type: 'custom', teamId: null, name: '' },
          away: { type: 'custom', teamId: null, name: '' }
        }
      });
    }
  }, [eventToEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Split the name into keys for nested state updates
    const keys = name.split('.');

    setFormData((prev) => {
      let updatedData = { ...prev };
      let temp = updatedData;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!temp[keys[i]]) temp[keys[i]] = {};
        temp = temp[keys[i]];
      }

      // Handle opponentTeam field separately to set null if empty
      if (name === 'opponentTeam') {
        temp[keys[keys.length - 1]] = value || null;
      } else {
        temp[keys[keys.length - 1]] = value;
      }

      return updatedData;
    });
  };

  // Handle location selection from LocationPicker
  const handleLocationSelect = (locationData) => {
    setFormData((prev) => {
      // CRITICAL: Auto-generate location name if not already set or is 'TBD'
      const currentName = prev.location.name?.trim();
      const needsAutoName = !currentName || currentName === 'TBD' || currentName === '';
      
      const autoName = needsAutoName 
        ? (locationData.street || 
           (locationData.city && locationData.state ? `${locationData.city}, ${locationData.state}` : '') ||
           locationData.city || 
           'Selected Location')
        : currentName;
      
      return {
        ...prev,
        location: {
          name: autoName,
          address: {
            street: locationData.street || '',
            city: locationData.city || '',
            state: locationData.state || '',
            zipCode: locationData.zipCode || '',
            coordinates: locationData.coordinates || {
              type: 'Point',
              coordinates: [0, 0]
            }
          }
        }
      };
    });
    setShowLocationPicker(false); // Close the modal
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user has management permissions
    if (!canManage) {
      alert('You need management permissions to create or edit events.');
      return;
    }

    try {
      // If coordinates are not set (manual entry), geocode the address
      if (!formData.location.address.coordinates?.coordinates?.[0] || 
          formData.location.address.coordinates.coordinates[0] === 0) {
        // Prepare the address string
        const address = `${formData.location.address.street}, ${formData.location.address.city}, ${formData.location.address.state} ${formData.location.address.zipCode}`;

        // Fetch coordinates from Geocoding API
        const geoResponse = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json`,
          {
            params: {
              address,
              key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
            },
          }
        );

        const { results } = geoResponse.data;
        if (results.length > 0) {
          const { lat, lng } = results[0].geometry.location;
          formData.location.address.coordinates = {
            type: 'Point',
            coordinates: [lng, lat],
          };
        } else {
          console.log('Could not find coordinates for the provided address.');
          
          formData.location.address.coordinates = {
            type: 'Point',
            coordinates: [0, 0],
          };
        }
      }

      // ✅ Prepare the payload - exclude teams and opponentTeam from spread, we'll add conditionally
      const { teams: _teams, opponentTeam: _opponentTeam, ...formDataWithoutTeams } = formData;
      
      // CRITICAL: Auto-generate location name if empty/TBD and we have address data
      // This is a fallback in case handleLocationSelect didn't set it properly
      let finalLocationName = formData.location.name?.trim();
      if (!finalLocationName || finalLocationName === 'TBD' || finalLocationName === '') {
        const addr = formData.location.address;
        const streetValue = addr.street?.trim();
        const cityValue = addr.city?.trim();
        const stateValue = addr.state?.trim();
        const coords = addr.coordinates?.coordinates;
        
        if (streetValue) {
          finalLocationName = streetValue;
        } else if (cityValue && stateValue) {
          finalLocationName = `${cityValue}, ${stateValue}`;
        } else if (cityValue) {
          finalLocationName = cityValue;
        } else if (coords && coords[0] !== 0 && coords[1] !== 0) {
          // We have coordinates but no address - use coordinates as name
          finalLocationName = `Location (${coords[1].toFixed(4)}, ${coords[0].toFixed(4)})`;
        } else {
          // No location data at all - keep as TBD
          finalLocationName = 'TBD';
        }
      }
      
      // Update location name in formData for submission
      formDataWithoutTeams.location = {
        ...formData.location,
        name: finalLocationName
      };
      
      // Helper to convert local datetime-local value to proper ISO string
      const convertLocalToISO = (localDateTimeString) => {
        // datetime-local input gives us "YYYY-MM-DDTHH:MM" in LOCAL timezone
        // We need to create a Date object that represents this local time
        const date = new Date(localDateTimeString);
        return date.toISOString();
      };

      const payload = {
        ...formDataWithoutTeams,
        programId,
        plannedAttendees: parseInt(formData.plannedAttendees, 10), // Convert to number
        dateTime: {
          start: convertLocalToISO(formData.startDateTime),
          end: convertLocalToISO(formData.endDateTime),
        },
      };

      // ✅ Only include teams if it's enabled AND teams have valid names (not empty)
      const hasValidTeams = teamSelectionEnabled && 
        formData.teams?.home?.name?.trim() && 
        formData.teams?.away?.name?.trim();

      if (hasValidTeams) {
        // Add teams to payload
        payload.teams = formData.teams;
        // ✅ DON'T send opponentTeam when using teams structure
        // Backend expects ObjectId for opponentTeam but we have string
        // The teams structure is the new format, so opponentTeam is not needed
      } else {
        // ✅ For events without team selection (e.g., Practice), only send opponentTeam if it exists
        // Only send if it's a valid ObjectId (not a string)
        if (formData.opponentTeam && formData.opponentTeam.length === 24) {
          payload.opponentTeam = formData.opponentTeam;
        }
      }

      // Send the data to backend
      if (eventToEdit) {
        // Update existing event
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/events/${eventToEdit._id}`,
          payload
        );
      } else {
        // Create new event
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/events/${programId}`,
          payload
        );
      }
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error adding or updating event:', error);
      console.error('🔍 Error Response Data:', error.response?.data);
      console.error('🔍 Error Response Status:', error.response?.status);
      console.error('🔍 Error Response Headers:', error.response?.headers);
      
      if (error.response?.status === 403) {
        alert('You don\'t have permission to perform this action.');
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Validation error';
        alert(`Validation Error: ${errorMsg}\n\nPlease check the console for details.`);
      } else {
        alert('An error occurred while saving the event. Please try again.');
      }
    }
  };

  // Function to handle deleting an event
  const handleDelete = async () => {
    // Check if user has management permissions
    if (!canManage) {
      alert('You need management permissions to delete events.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_URL}/api/events/${eventToEdit._id}`
        );
        onClose();
        window.location.reload();
      } catch (error) {
        console.error('Error deleting event:', error);
        if (error.response?.status === 403) {
          alert('You don\'t have permission to delete this event.');
        } else {
          alert('An error occurred while deleting the event. Please try again.');
        }
      }
    }
  };

  return createPortal(
    <div className={modalStyles.modalOverlay}>
      <div className={`${modalStyles.modal} ${isMaximized ? modalStyles.maximized : ''}`}>
        {/* Modal Header */}
        <div className={modalStyles.modalHeader}>
          <div className={modalStyles.modalTitle}>
            <FaCalendarAlt className={modalStyles.modalTitleIcon} />
            {eventToEdit ? 'Edit Event' : 'Add New Event'}
            {isMaximized && <span style={{ fontSize: '0.8em', marginLeft: '10px', opacity: 0.8 }}>(Maximized)</span>}
          </div>
          <div className={modalStyles.headerControls}>
            <button 
              className={modalStyles.maximizeButton}
              onClick={() => {
                console.log('Maximize button clicked, current state:', isMaximized);
                setIsMaximized(!isMaximized);
              }}
              aria-label={isMaximized ? "Restore" : "Maximize"}
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? <FaCompress className={modalStyles.maximizeIcon} /> : <FaExpand className={modalStyles.maximizeIcon} />}
            </button>
            <button className={modalStyles.closeButton} onClick={onClose} aria-label="Close">
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className={modalStyles.modalBody}>
          <form className={modalStyles.form} onSubmit={handleSubmit}>
            {/* Show completed event banner */}
            {eventToEdit && formData.status === 'Completed' && (
              <div style={{
                background: 'rgba(164, 235, 63, 0.1)',
                border: '1px solid rgba(164, 235, 63, 0.3)',
                borderRadius: 'var(--border-radius)',
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--accent)',
                fontWeight: 'var(--font-semibold)'
              }}>
                <FaInfoCircle style={{ marginRight: '8px', fontSize: '18px' }} />
                This event is completed. All fields are read-only.
              </div>
            )}
            <div className={modalStyles.formRow}>
          {/* Title */}
          <div className={modalStyles.formGroup}>
                <label className={modalStyles.formLabel}>
                  <FaInfoCircle className={modalStyles.formLabelIcon} />
                  Title
                </label>
            <input
              type="text"
              name="title"
                  className={modalStyles.formInput}
              value={formData.title}
              onChange={handleInputChange}
              disabled={eventToEdit && formData.status === 'Completed'}
              required
            />
          </div>

          {/* Type */}
          <div className={modalStyles.formGroup}>
                <label className={modalStyles.formLabel}>
                  <FaCalendarAlt className={modalStyles.formLabelIcon} />
                  Type
                </label>
            <select
              name="type"
                  className={modalStyles.formSelect}
              value={formData.type}
              onChange={handleInputChange}
              disabled={eventToEdit && formData.status === 'Completed'}
              required
            >
              <option value="Practice">Practice</option>
              <option value="Friendly-Game">Friendly Game</option>
              <option value="Tournament">Tournament</option>
            </select>
              </div>
          </div>

            {/* ✅ NEW: Team Selection Section - Only show for game types and if enabled */}
            {teamSelectionEnabled && (formData.type === 'Friendly-Game' || formData.type === 'Tournament') && (
              <div className={modalStyles.formGroupFull} style={{ marginBottom: 'var(--space-6)' }}>
                <div style={{ 
                  padding: 'var(--space-4)', 
                  background: 'rgba(164, 235, 63, 0.05)',
                  borderRadius: 'var(--border-radius)',
                  border: '1px solid rgba(164, 235, 63, 0.2)'
                }}>
                  <h3 style={{ 
                    color: 'var(--accent)', 
                    marginBottom: 'var(--space-4)',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-semibold)'
                  }}>
                    <FaUsers style={{ marginRight: 'var(--space-2)' }} />
                    Team Selection
                  </h3>
                  
                  <div className={modalStyles.formRow}>
                    <TeamSelector
                      position="home"
                      value={formData.teams.home}
                      onChange={(homeTeam) => setFormData(prev => ({
                        ...prev,
                        teams: { ...prev.teams, home: homeTeam }
                      }))}
                      teams={programTeams}
                      label="Home Team"
                    />
                    
                    <TeamSelector
                      position="away"
                      value={formData.teams.away}
                      onChange={(awayTeam) => setFormData(prev => ({
                        ...prev,
                        teams: { ...prev.teams, away: awayTeam },
                        opponentTeam: awayTeam.name // ✅ Update legacy field too
                      }))}
                      teams={programTeams}
                      label="Away Team / Opponent"
                      allowCustom={true}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={modalStyles.formRow}>
          {/* Start Date & Time */}
          <div className={modalStyles.formGroup}>
                <label className={modalStyles.formLabel}>
                  <FaCalendarAlt className={modalStyles.formLabelIcon} />
                  Start Date & Time
                  <span style={{ 
                    fontSize: '10px', 
                    marginLeft: '8px', 
                    opacity: 0.7,
                    fontWeight: 'normal'
                  }}>
                    ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                  </span>
                </label>
            <input
              type="datetime-local"
              name="startDateTime"
                  className={modalStyles.formInput}
              value={formData.startDateTime}
              onChange={handleInputChange}
              disabled={eventToEdit && formData.status === 'Completed'}
              required
            />
          </div>

          {/* End Date & Time */}
          <div className={modalStyles.formGroup}>
                <label className={modalStyles.formLabel}>
                  <FaCalendarAlt className={modalStyles.formLabelIcon} />
                  End Date & Time
                  <span style={{ 
                    fontSize: '10px', 
                    marginLeft: '8px', 
                    opacity: 0.7,
                    fontWeight: 'normal'
                  }}>
                    ({Intl.DateTimeFormat().resolvedOptions().timeZone})
                  </span>
                </label>
            <input
              type="datetime-local"
              name="endDateTime"
                  className={modalStyles.formInput}
              value={formData.endDateTime}
              onChange={handleInputChange}
              disabled={eventToEdit && formData.status === 'Completed'}
              required
            />
              </div>
          </div>

            {/* Planned Attendees */}
            <div className={modalStyles.formGroup}>
              <label className={modalStyles.formLabel}>
                <FaUsers className={modalStyles.formLabelIcon} />
                Planned Attendees
              </label>
              <input
                type="number"
                name="plannedAttendees"
                className={modalStyles.formInput}
                value={formData.plannedAttendees}
                onChange={handleInputChange}
                disabled={eventToEdit && formData.status === 'Completed'}
                required
              />
            </div>

            {/* Location Section */}
            <div className={modalStyles.locationSection}>
              <div className={modalStyles.locationHeader}>
                <label className={modalStyles.formLabel}>
                  <FaMapMarkerAlt className={modalStyles.formLabelIcon} />
                  Location
                </label>
                {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
                  <button
                    type="button"
                    className={modalStyles.pickLocationButton}
                    onClick={() => setShowLocationPicker(true)}
                    disabled={eventToEdit && formData.status === 'Completed'}
                  >
                    <FaMapMarkerAlt />
                    {formData.location.address.city ? 'Change on Map' : 'Pick on Map'}
                  </button>
                )}
              </div>

              {/* Selected Location Display */}
              {(formData.location.address.city || formData.location.address.state || formData.location.address.street) ? (
                <div className={modalStyles.selectedLocation}>
                  <FaMapMarkerAlt className={modalStyles.locationIcon} />
                  <span>
                    {[formData.location.address.city, formData.location.address.state, formData.location.address.country]
                      .filter(val => typeof val === 'string' && val)
                      .join(', ')}
                  </span>
                </div>
              ) : (
                <div className={modalStyles.selectedLocation}>
                  <FaMapMarkerAlt className={modalStyles.locationIcon} />
                  <span>No location selected</span>
                </div>
              )}

              {/* Location Name */}
              <div className={modalStyles.formGroup}>
                <label className={modalStyles.formLabel}>
                  <FaMapMarkerAlt className={modalStyles.formLabelIcon} />
                  Location Name
                </label>
                <input
                  type="text"
                  name="location.name"
                  className={modalStyles.formInput}
                  value={formData.location.name}
                  onChange={handleInputChange}
                  disabled={eventToEdit && formData.status === 'Completed'}
                  placeholder="e.g., Main Training Field"
                  required
                />
              </div>

              {/* Street */}
              <div className={modalStyles.formGroup}>
                <label className={modalStyles.formLabel}>
                  <FaMapMarkerAlt className={modalStyles.formLabelIcon} />
                  Street Address
                </label>
                <input
                  type="text"
                  name="location.address.street"
                  className={modalStyles.formInput}
                  value={formData.location.address.street}
                  onChange={handleInputChange}
                  disabled={eventToEdit && formData.status === 'Completed'}
                  placeholder="123 Main Street"
                  required
                />
              </div>

              {/* City, State, Zip */}
              <div className={modalStyles.formRow}>
                <div className={modalStyles.formGroup}>
                  <label className={modalStyles.formLabel}>
                    <FaMapMarkerAlt className={modalStyles.formLabelIcon} />
                    City
                  </label>
                  <input
                    type="text"
                    name="location.address.city"
                    className={modalStyles.formInput}
                    value={formData.location.address.city}
                    onChange={handleInputChange}
                    disabled={eventToEdit && formData.status === 'Completed'}
                    placeholder="San Diego"
                    required
                  />
                </div>

                <div className={modalStyles.formGroup}>
                  <label className={modalStyles.formLabel}>
                    <FaMapMarkerAlt className={modalStyles.formLabelIcon} />
                    State
                  </label>
                  <input
                    type="text"
                    name="location.address.state"
                    className={modalStyles.formInput}
                    value={formData.location.address.state}
                    onChange={handleInputChange}
                    disabled={eventToEdit && formData.status === 'Completed'}
                    placeholder="CA"
                    required
                  />
                </div>
              </div>

              <div className={modalStyles.formRow}>
                <div className={modalStyles.formGroup}>
                  <label className={modalStyles.formLabel}>
                    <FaMapMarkerAlt className={modalStyles.formLabelIcon} />
                    Zip Code
                  </label>
                  <input
                    type="text"
                    name="location.address.zipCode"
                    className={modalStyles.formInput}
                    value={formData.location.address.zipCode}
                    onChange={handleInputChange}
                    disabled={eventToEdit && formData.status === 'Completed'}
                    placeholder="92101"
                    required
                  />
                </div>

                {/* Status */}
                <div className={modalStyles.formGroup}>
                  <label className={modalStyles.formLabel}>
                    <FaCalendarAlt className={modalStyles.formLabelIcon} />
                    Status
                  </label>
                  <select
                    name="status"
                    className={modalStyles.formSelect}
                    value={formData.status}
                    onChange={handleInputChange}
                    disabled={eventToEdit && formData.status === 'Completed'}
                  >
                    <option value="Planned">Planned</option>
                    <option value="Active">Active</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className={modalStyles.formGroupFull}>
              <label className={modalStyles.formLabel}>
                <FaInfoCircle className={modalStyles.formLabelIcon} />
                Description
              </label>
              <textarea
                name="description"
                className={modalStyles.formTextarea}
                value={formData.description}
                onChange={handleInputChange}
                disabled={eventToEdit && formData.status === 'Completed'}
                required
              />
            </div>

          </form>

          {/* Location Picker Modal */}
          {showLocationPicker && (
            <div className={modalStyles.modalOverlay} onClick={() => setShowLocationPicker(false)}>
              <div className={modalStyles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={modalStyles.modalHeader}>
                  <h3>
                    <FaMapMarkerAlt />
                    Pick Location
                  </h3>
                  <button
                    type="button"
                    className={modalStyles.modalCloseButton}
                    onClick={() => setShowLocationPicker(false)}
                  >
                    ×
                  </button>
                </div>
                <div className={modalStyles.modalBody}>
                  <LocationPicker
                    value={{
                      coordinates: formData.location.address.coordinates,
                      street: formData.location.address.street,
                      city: formData.location.address.city,
                      state: formData.location.address.state,
                      zipCode: formData.location.address.zipCode,
                      country: formData.location.address.country
                    }}
                    onLocationSelect={handleLocationSelect}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Fixed Button Container - Always at bottom */}
          <div className={modalStyles.buttonContainer}>
            {/* Hide Update button for completed events */}
            {!(eventToEdit && formData.status === 'Completed') && (
              <button 
                type="submit" 
                className={modalStyles.submitButton}
                onClick={handleSubmit}
              >
                {eventToEdit ? 'Update Event' : 'Add Event'}
              </button>
            )}
            <button
              type="button"
              className={modalStyles.cancelButton}
              onClick={onClose}
            >
              {eventToEdit && formData.status === 'Completed' ? 'Close' : 'Cancel'}
            </button>
            {/* Show Delete button only when editing and not completed */}
            {eventToEdit && formData.status !== 'Completed' && (
              <button
                type="button"
                className={modalStyles.deleteButton}
                onClick={handleDelete}
              >
                Delete Event
              </button>
            )}
            {/* Show info message for completed events */}
            {eventToEdit && formData.status === 'Completed' && (
              <div className={modalStyles.completedInfo}>
                <FaInfoCircle style={{ marginRight: '8px' }} />
                This event is completed and cannot be edited or deleted.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EventModal;
